import { AutocompleteFilter } from '@shigo-ui/ui';

import { dashboardT } from '../../../lib/messages';
import { useSupplyAppsReferences } from './hooks';
import type { SupplyAppsListFilter } from './model';
import { toSupplyAppsOptions } from './referenceOptions';

interface AppsFiltersProps {
  filter: SupplyAppsListFilter;
  onPublishersChange?: ((publisherIds: string[]) => void) | undefined;
  onPlatformsChange: (platformIds: string[]) => void;
  onIabCategoriesChange: (iabCategoryIds: string[]) => void;
  publisherScoped?: boolean;
}

export function AppsFilters({
  filter,
  onPublishersChange,
  onPlatformsChange,
  onIabCategoriesChange,
  publisherScoped,
}: AppsFiltersProps) {
  const references = useSupplyAppsReferences({ publishersEnabled: !publisherScoped });
  const lookupError =
    references.publishers.isError ||
    references.platforms.isError ||
    references.iabCategories.isError;

  return (
    <section
      aria-label={dashboardT('supplyApps.filters')}
      className="border-border bg-card grid min-w-0 gap-3 rounded-md border p-3 md:grid-cols-2 xl:grid-cols-3"
    >
      {onPublishersChange ? (
        <AutocompleteFilter
          label={dashboardT('supplyApps.publishersFilter')}
          options={toSupplyAppsOptions(references.publishers.data ?? [])}
          selectedIds={filter.publisherIds}
          onSelectedIdsChange={onPublishersChange}
          loading={references.publishers.isFetching}
          placeholder={dashboardT('supplyApps.publishersFilter')}
          emptyMessage={dashboardT('supplyApps.lookupEmpty')}
          className="bg-card"
        />
      ) : null}
      <AutocompleteFilter
        label={dashboardT('supplyApps.platformsFilter')}
        options={toSupplyAppsOptions(references.platforms.data ?? [])}
        selectedIds={filter.platformIds}
        onSelectedIdsChange={onPlatformsChange}
        loading={references.platforms.isFetching}
        placeholder={dashboardT('supplyApps.platformsFilter')}
        emptyMessage={dashboardT('supplyApps.lookupEmpty')}
        className="bg-card"
      />
      <AutocompleteFilter
        label={dashboardT('supplyApps.iabCategoriesFilter')}
        options={toSupplyAppsOptions(references.iabCategories.data ?? [])}
        selectedIds={filter.iabCategoryIds}
        onSelectedIdsChange={onIabCategoriesChange}
        loading={references.iabCategories.isFetching}
        placeholder={dashboardT('supplyApps.iabCategoriesFilter')}
        emptyMessage={dashboardT('supplyApps.lookupEmpty')}
        className="bg-card"
      />
      {lookupError ? (
        <p role="alert" className="text-destructive text-xs md:col-span-2 xl:col-span-3">
          {dashboardT('supplyApps.lookupError')}
        </p>
      ) : null}
    </section>
  );
}
